import AuthService from 'common/auth.service';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, CardBody, CardTitle, Col, Row } from 'reactstrap';
import { getCurrentUserData, titleCaseIfExists } from 'utils/helper';

const HeartBeats = () => {
  const [heartbeatsType, setHeartbeatsType] = useState([]);
  const [loadingStates, setLoadingStates] = useState({});
  const [errorStates, setErrorStates] = useState({});
  const isInitialFetchDone = useRef(false);

  const commonHeartbeats = [
    'admin-tools',
    'notification',
    'common',
    'user',
    'cms',
    'forum',
    'subscription',
    'community',
  ];

  const smkHeartbeats = ['school', 'college', 'ptable', 'worksheet', 'calculator', 'whiteboard', 'worksheet-generator'];
  const tpzHeartbeats = ['baby-name', 'patient-tracking', 'document', 'nutrition', 'achievement'];
  const otherSmkHeartbeats = ['rha'];
  const otherTpzHeartbeats = ['ask-tpz', 'healthcare'];

  const auth = new AuthService();
  const { apiKey, stgURL } = getCurrentUserData();

  useEffect(() => {
    const defaultStates = [
      ...commonHeartbeats.map((c) => ({ type: 'common-smk', name: c })),
      ...commonHeartbeats.map((c) => ({ type: 'common-tpz', name: c })),
      ...smkHeartbeats.map((c) => ({ type: 'smk', name: c })),
      ...tpzHeartbeats.map((c) => ({ type: 'tpz', name: c })),
      ...otherSmkHeartbeats.map((c) => ({ type: 'others-smk', name: c })),
      ...otherTpzHeartbeats.map((c) => ({ type: 'others-tpz', name: c })),
    ];
    setHeartbeatsType(defaultStates);
  }, []);

  const apiWithTimeout = (apiCall, timeout = 5000) => {
    return Promise.race([
      apiCall,
      new Promise((_, reject) => setTimeout(() => reject(new Error('Request timed out')), timeout)),
    ]);
  };

  const callHeartbeatApi = async (elem) => {
    const keyName = `${elem.name}_${elem.type}`;

    if (loadingStates[keyName]) return;

    let apiPrefix = stgURL;
    let updatedAPIKey = apiKey;

    if (elem?.type?.includes('smk')) {
      apiPrefix = process.env.REACT_APP_SCHOOLMYKIDS_API_URL;
      updatedAPIKey = process.env.REACT_APP_SCHOOLMYKIDS_API_KEY;
    }

    if (elem?.type?.includes('tpz')) {
      apiPrefix = process.env.REACT_APP_THEPARENTZ_API_URL;
      updatedAPIKey = process.env.REACT_APP_THEPARENTZ_API_KEY;
    }

    setLoadingStates((prev) => ({ ...prev, [keyName]: true }));
    setErrorStates((prev) => ({ ...prev, [keyName]: false }));

    try {
      const response = await apiWithTimeout(
        fetch(`${apiPrefix}/${elem.name}/heartbeat`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${auth.getSessionCookie()}`,
            'x-api-key': updatedAPIKey,
          },
        })
      );

      if (!response.ok) throw new Error(`Request failed with status: ${response.status}`);

      const data = await response.json();

      if (elem.type.includes('common')) {
        setHeartbeatsType((prev) =>
          prev.map((e) => (e.name === elem.name && e.type === elem.type ? { ...elem, live: data.alive || false } : e))
        );
      } else {
        setHeartbeatsType((prev) =>
          prev.map((e) => (e.name === elem.name ? { ...elem, live: data.alive || false } : e))
        );
      }
    } catch (err) {
      setErrorStates((prev) => ({ ...prev, [keyName]: true }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, [keyName]: false }));
    }
  };

  const fetchAllApis = (heartbeats) => {
    heartbeats.forEach((elem) => callHeartbeatApi(elem));
  };

  useEffect(() => {
    if (!isInitialFetchDone.current && heartbeatsType.length > 0) {
      fetchAllApis(heartbeatsType);
      isInitialFetchDone.current = true;
    }
  }, [heartbeatsType]);

  const renderSection = (title, filterKey) => {
    return (
      <Col md={4}>
        <h5 className="border-bottom mb-2 bg-light text-center py-3 rounded-2">{title}</h5>
        <Row>
          {heartbeatsType
            .filter((e) => e.type === filterKey)
            .map((elem, index) => (
              <Col md={12} className="d-flex align-items-center py-2 mb-2" key={index}>
                <i
                  style={{ fontSize: '24px' }}
                  className={`me-2 ${
                    elem.live ? 'bx bxs-check-circle text-success' : 'bx bxs-error-circle text-danger'
                  }`}
                />
                <div className="d-flex align-items-center justify-content-between w-100">
                  <h5 className="ms-3 mb-0">{titleCaseIfExists(elem.name)}</h5>
                  <Button
                    color={'light'}
                    className="btn-sm me-2"
                    onClick={() => callHeartbeatApi(elem)}
                    disabled={loadingStates[elem.name]}>
                    {loadingStates[elem.name] ? 'Checking...' : 'Check'}
                  </Button>
                </div>
              </Col>
            ))}
        </Row>
      </Col>
    );
  };

  return (
    <>
      <Card className="mb-4">
        <CardTitle tag="h2" className="text-center border-bottom py-3 mb-2 text-primary">
          SMK Heartbeats
        </CardTitle>
        <CardBody>
          <Row>
            {renderSection('Common', 'common-smk')}
            {renderSection('SchoolMyKids', 'smk')}
            {renderSection('Others', 'others-smk')}
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardTitle tag="h2" className="text-center border-bottom py-3 mb-2 text-primary">
          TPZ Heartbeats
        </CardTitle>
        <CardBody>
          <Row>
            {renderSection('Common', 'common-tpz')}
            {renderSection('TheParentz', 'tpz')}
            {renderSection('Others', 'others-tpz')}
          </Row>
        </CardBody>
      </Card>
    </>
  );
};

export default HeartBeats;
